// Feature Requests Management Page
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Tabs,
  Tab,
  Chip,
  TextField,
  InputAdornment,
  Card,
  CardContent,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  LinearProgress,
  Avatar,
  AvatarGroup,
  Tooltip,
} from '@mui/material';
import {
  Add,
  Search,
  MoreVert,
  ThumbUp,
  Lightbulb,
  Schedule,
  CheckCircle,
  Block,
  Public,
  PublicOff,
} from '@mui/icons-material';
import { useOrganization } from '@/contexts/OrganizationContext';
import { useAuth } from '@/contexts/AuthContext';
import { usePermissions } from '@/hooks/usePermissions';
import { useToast } from '@/contexts/ToastContext';
import { featuresService } from '@/services/collections/features';
import { formatRelativeTime } from '@/services/firebase/firestore';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import type { FeatureRequest, FeatureStatus } from '@/types';

const STATUS_CONFIG: Record<FeatureStatus, { label: string; color: any; icon: React.ReactNode }> = {
  under_review: { label: 'Under Review', color: 'default', icon: <Lightbulb /> },
  planned: { label: 'Planned', color: 'info', icon: <Schedule /> },
  in_progress: { label: 'In Progress', color: 'warning', icon: <Schedule /> },
  released: { label: 'Released', color: 'success', icon: <CheckCircle /> },
  declined: { label: 'Declined', color: 'error', icon: <Block /> },
};

export const FeaturesPage = () => {
  const navigate = useNavigate();
  const { organization, project } = useOrganization();
  const { user } = useAuth();
  const { canManageFeatures, hasFeatureVoting } = usePermissions();
  const { showSuccess, showError } = useToast();

  const [features, setFeatures] = useState<FeatureRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [menuAnchor, setMenuAnchor] = useState<{ el: HTMLElement; feature: FeatureRequest } | null>(null);

  // Fetch features
  const fetchFeatures = async () => {
    if (!organization || !project) return;

    setLoading(true);
    try {
      let data: FeatureRequest[] = [];

      switch (tab) {
        case 0: // All
          data = await featuresService.getByProject(organization.id, project.id, []);
          break;
        case 1: // Under Review
          data = await featuresService.getByStatus(organization.id, project.id, 'under_review');
          break;
        case 2: // Planned
          data = await featuresService.getByStatus(organization.id, project.id, 'planned');
          break;
        case 3: // In Progress
          data = await featuresService.getByStatus(organization.id, project.id, 'in_progress');
          break;
        case 4: // Released
          data = await featuresService.getByStatus(organization.id, project.id, 'released');
          break;
      }

      setFeatures(data);
    } catch (error) {
      console.error('Error fetching features:', error);
      showError('Failed to load feature requests');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFeatures();
  }, [organization, project, tab]);

  // Filter features
  const filteredFeatures = features.filter((feature) => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      feature.title.toLowerCase().includes(query) ||
      feature.description.toLowerCase().includes(query)
    );
  });

  // Update status
  const handleStatusChange = async (feature: FeatureRequest, status: FeatureStatus) => {
    try {
      await featuresService.updateStatus(feature.id, status);
      showSuccess('Status updated');
      fetchFeatures();
    } catch (error) {
      showError('Failed to update status');
    }
    setMenuAnchor(null);
  };

  // Toggle public visibility
  const handleTogglePublic = async (feature: FeatureRequest) => {
    try {
      await featuresService.setPublic(feature.id, !feature.isPublic);
      showSuccess(feature.isPublic ? 'Made private' : 'Made public');
      fetchFeatures();
    } catch (error) {
      showError('Failed to update visibility');
    }
    setMenuAnchor(null);
  };

  // Get max votes for progress bar
  const maxVotes = Math.max(...features.map((f) => f.voteCount), 1);

  // Check plan access
  if (!hasFeatureVoting) {
    return (
      <Box sx={{ textAlign: 'center', py: 8 }}>
        <Lightbulb sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
        <Typography variant="h5" gutterBottom>
          Feature Requests
        </Typography>
        <Typography color="text.secondary" sx={{ mb: 3 }}>
          Upgrade to Pro or higher to access Feature Voting.
        </Typography>
        <Button variant="contained" onClick={() => navigate('/app/settings/billing')}>
          Upgrade Plan
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight="bold">
            Feature Requests
          </Typography>
          <Typography color="text.secondary">
            Manage and prioritize feature requests from your users
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            onClick={() => window.open(`/p/${organization?.slug}/roadmap`, '_blank')}
          >
            View Public Roadmap
          </Button>
          {canManageFeatures && (
            <Button variant="contained" startIcon={<Add />} onClick={() => navigate('/app/features/new')}>
              Add Feature
            </Button>
          )}
        </Stack>
      </Box>

      {/* Stats */}
      <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
        <Card sx={{ flex: 1 }}>
          <CardContent sx={{ py: 2 }}>
            <Typography variant="h4" fontWeight="bold">
              {features.length}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Total Requests
            </Typography>
          </CardContent>
        </Card>
        <Card sx={{ flex: 1 }}>
          <CardContent sx={{ py: 2 }}>
            <Typography variant="h4" fontWeight="bold">
              {features.reduce((sum, f) => sum + f.voteCount, 0)}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Total Votes
            </Typography>
          </CardContent>
        </Card>
        <Card sx={{ flex: 1 }}>
          <CardContent sx={{ py: 2 }}>
            <Typography variant="h4" fontWeight="bold">
              {features.filter((f) => f.status === 'planned' || f.status === 'in_progress').length}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              In Progress
            </Typography>
          </CardContent>
        </Card>
        <Card sx={{ flex: 1 }}>
          <CardContent sx={{ py: 2 }}>
            <Typography variant="h4" fontWeight="bold">
              {features.filter((f) => f.status === 'released').length}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Released
            </Typography>
          </CardContent>
        </Card>
      </Stack>

      {/* Tabs */}
      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label="All" />
        <Tab label="Under Review" />
        <Tab label="Planned" />
        <Tab label="In Progress" />
        <Tab label="Released" />
      </Tabs>

      {/* Search */}
      <TextField
        placeholder="Search features..."
        size="small"
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Search />
            </InputAdornment>
          ),
        }}
        sx={{ mb: 3, width: 300 }}
      />

      {/* Features List */}
      {loading ? (
        <LoadingSpinner message="Loading features..." />
      ) : filteredFeatures.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Lightbulb sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            No feature requests found
          </Typography>
        </Box>
      ) : (
        <Stack spacing={2}>
          {filteredFeatures.map((feature) => (
            <Card
              key={feature.id}
              sx={{ cursor: 'pointer', '&:hover': { boxShadow: 2 } }}
              onClick={() => navigate(`/app/features/${feature.id}`)}
            >
              <CardContent>
                <Box sx={{ display: 'flex', gap: 3 }}>
                  {/* Vote Count */}
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      minWidth: 60,
                    }}
                  >
                    <IconButton
                      size="small"
                      onClick={async (e) => {
                        e.stopPropagation();
                        if (!user) return;
                        const hasVoted = featuresService.hasVoted(feature, user.uid);
                        try {
                          if (hasVoted) {
                            await featuresService.unvote(feature.id, user.uid);
                          } else {
                            await featuresService.vote(feature.id, user.uid);
                          }
                          fetchFeatures();
                        } catch {
                          showError('Failed to vote');
                        }
                      }}
                      sx={{
                        color: user && featuresService.hasVoted(feature, user.uid) ? 'primary.main' : 'inherit',
                      }}
                    >
                      <ThumbUp />
                    </IconButton>
                    <Typography variant="h6" fontWeight="bold">
                      {feature.voteCount}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      votes
                    </Typography>
                  </Box>

                  {/* Content */}
                  <Box sx={{ flex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <Typography variant="h6">{feature.title}</Typography>
                      <Chip
                        label={STATUS_CONFIG[feature.status].label}
                        size="small"
                        color={STATUS_CONFIG[feature.status].color}
                        icon={STATUS_CONFIG[feature.status].icon as any}
                      />
                      {feature.isPublic ? (
                        <Tooltip title="Visible on public roadmap">
                          <Public fontSize="small" color="action" />
                        </Tooltip>
                      ) : (
                        <Tooltip title="Private">
                          <PublicOff fontSize="small" color="disabled" />
                        </Tooltip>
                      )}
                    </Box>

                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        mb: 2,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                      }}
                    >
                      {feature.description}
                    </Typography>

                    {/* Vote Progress */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Box sx={{ flex: 1, maxWidth: 200 }}>
                        <LinearProgress
                          variant="determinate"
                          value={(feature.voteCount / maxVotes) * 100}
                          sx={{ height: 6, borderRadius: 3 }}
                        />
                      </Box>

                      {feature.voterIds?.length > 0 && (
                        <AvatarGroup max={3} sx={{ '& .MuiAvatar-root': { width: 24, height: 24, fontSize: 12 } }}>
                          {feature.voterIds.slice(0, 3).map((id, i) => (
                            <Avatar key={i}>{id[0]?.toUpperCase()}</Avatar>
                          ))}
                        </AvatarGroup>
                      )}

                      <Typography variant="caption" color="text.secondary">
                        {formatRelativeTime(feature.createdAt)}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Actions */}
                  {canManageFeatures && (
                    <IconButton
                      onClick={(e) => {
                        e.stopPropagation();
                        setMenuAnchor({ el: e.currentTarget, feature });
                      }}
                    >
                      <MoreVert />
                    </IconButton>
                  )}
                </Box>
              </CardContent>
            </Card>
          ))}
        </Stack>
      )}

      {/* Feature Menu */}
      <Menu
        anchorEl={menuAnchor?.el}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
      >
        <MenuItem disabled>
          <Typography variant="caption" color="text.secondary">
            Change Status
          </Typography>
        </MenuItem>
        {Object.entries(STATUS_CONFIG).map(([status, config]) => (
          <MenuItem
            key={status}
            selected={menuAnchor?.feature.status === status}
            onClick={() => handleStatusChange(menuAnchor!.feature, status as FeatureStatus)}
          >
            {config.icon}
            <Typography sx={{ ml: 1 }}>{config.label}</Typography>
          </MenuItem>
        ))}
        <MenuItem divider />
        <MenuItem onClick={() => handleTogglePublic(menuAnchor!.feature)}>
          {menuAnchor?.feature.isPublic ? (
            <>
              <PublicOff sx={{ mr: 1 }} /> Make Private
            </>
          ) : (
            <>
              <Public sx={{ mr: 1 }} /> Make Public
            </>
          )}
        </MenuItem>
      </Menu>
    </Box>
  );
};
